import React, { useEffect, useMemo, useState } from 'react';
import Table, { TableProps } from '@cloudscape-design/components/table';
import Input from '@cloudscape-design/components/input';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import { getApiHeader } from 'src/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Spinner from '@cloudscape-design/components/spinner';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import FormField from '@cloudscape-design/components/form-field';
import Select from '@cloudscape-design/components/select';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Modal from '@cloudscape-design/components/modal';

interface JudgementDataResponse {
    chestNumberList: string[];
    eventCategory: string;
    eventType: string;
    grade: string;
    participants: Record<number, {
        chestNumber: string;
        criterias: Record<string, string>;
        judges: Record<string, string>;
        participantId: string | undefined;
        groupId: string;
        participantName: string;
        score: Record<string, {
            criteria: string;
            criteriaId: string;
            maxScore: number;
            judges: Record<string, {
                judge: string;
                judgeId: string;
                score: string;
            }>
        }>
    }>
    stageId: string;
    eventId: string;
}

type JudgeScoreType = {
    [key: string]: any;
};

type CriteriaType = {
    criteriaId: string;
    criteriaName: string;
    maxScore: number;
};

type ItemsType = JudgeScoreType & CriteriaType;

interface ScoreDetailsProps {
    eventID: string;
    eventCategory: string;
    grade: string;
}

interface ResultList {
    judgeId: string;
    criteriaId: string;
    score: string;
}

interface SaveScoreResultRequest {
    eventID?: string;
    stageID?: string;
    groupID?: string;
    participantID?: string;
    chestNumber?: string;
    resultList?: ResultList[];
}

const ValueWithLabel = ({ label, children }: { label: string; children: React.ReactElement }) => (
    <div>
        <Box variant="awsui-key-label">{label}</Box>
        <div>{children}</div>
    </div>
);

const MAX_SCORE = 10;

export const ScoreDetails: React.FC<ScoreDetailsProps> = ({
    eventID,
    eventCategory,
    grade,
}) => {

    const [columnDefinitions, setColumnDefinitions] = useState<TableProps.ColumnDefinition<ItemsType>[]>([]);
    const [tableItems, setTableItems] = useState<ItemsType[]>([]);
    const [chestNumber, setChestNumber] = useState<string[]>();
    const [selectedChestNumber, setSelectedChestNumber] = useState<OptionDefinition>({ label: 'Select chest number', value: '' });
    const [saveScoreResultRequest, setSaveScoreResultRequest] = useState<SaveScoreResultRequest>({
        eventID
    });
    const [allColumns, setAllColumns] = useState<{
        id: string;
        name: string;
    }[]>();
    const [judgementData, setJudgementData] = useState<JudgementDataResponse>();
    const [isTableValid, setTableValid] = useState(true);
    const [isModalVisible ,setModalVisible] = useState(false);

    const headers = getApiHeader();

    const { data, isLoading, refetch } = useQuery<JudgementDataResponse>({
        queryKey: ['group-checkin'],
        queryFn: async () => {
            const result = await axios.post('https://hqskmnem8b.execute-api.us-east-1.amazonaws.com/Prod/getEventJudgementDetails',
                {
                    eventCategory,
                    grade
                },
                {
                    headers
                }
            );
            return result.data.result;
        },
        staleTime: 0,
        enabled: !!(eventCategory && grade)
    });

    useEffect(() => {
        setJudgementData(data);
    }, [data]);

    const handleScoreChange = useMemo(() => (score: any, judgeId: string, criteriaId: string, maxScore: any) => {
        console.log('log: isNaN(score)', isNaN(score));
        // Validate against MAX_SCORE instead of actual maxScore
        if (score > MAX_SCORE || score < 0 || isNaN(score)) {
            setTableValid(false);
        } else {
            setTableValid(true);
        }
        setSaveScoreResultRequest((prevScore) => {
            // Filter the result list to prevent duplicate value for the same pair
            const filterResultList = prevScore.resultList?.filter((item) => !(item.criteriaId == criteriaId && item.judgeId == judgeId));
            return {
                ...prevScore,
                resultList: [
                    ...filterResultList ?? [],
                    {
                        criteriaId,
                        judgeId,
                        score
                    }
                ]
            }
        });

        setTableItems((prevTableItems) => {
            return prevTableItems.map((item) => {
                if (item.criteriaId === criteriaId) {
                    return {
                        ...item,
                        [judgeId]: {
                            score,
                            judgeId,
                            maxScore
                        }
                    }
                } else {
                    return item;
                }
            });
        });
    }, [tableItems]);

    const { mutate, data: saveScoreResponse, error, isSuccess, isPending, reset: resetData } = useMutation({
        mutationFn: (data: SaveScoreResultRequest) => {
            return axios.post('https://v63t2rc5ae.execute-api.us-east-1.amazonaws.com/Prod/saveResultsForEvent',
                data, {
                headers
            });
        }
    });

    const saveScores = () => {
        if (!isTableValid) {
            setModalVisible(true);
            return false;
        } else {
            // Transform scores before saving: enteredValue * actualMaxScore / MAX_SCORE
            const transformedRequest = {
                ...saveScoreResultRequest,
                resultList: saveScoreResultRequest.resultList?.map((result) => {
                    // Find the actual maxScore for this criteria
                    const criteriaItem = tableItems.find(item => item.criteriaId === result.criteriaId);
                    const actualMaxScore = criteriaItem?.maxScore || 100;
                    const enteredScore = parseFloat(result.score);
                    const transformedScore = (enteredScore * actualMaxScore) / MAX_SCORE;
                    
                    return {
                        ...result,
                        score: transformedScore.toString()
                    };
                })
            };
            mutate(transformedRequest);
        }
    };

    useEffect(() => {
        if (isSuccess) {
            refetch();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (judgementData?.chestNumberList.length) {
            setChestNumber(judgementData?.chestNumberList);
            setSaveScoreResultRequest((prevScore) => ({
                ...prevScore,
                stageID: judgementData.stageId,
                eventID: judgementData.eventId
            }));
        }
    }, [judgementData?.chestNumberList]);

    useEffect(() => {
        if (selectedChestNumber.value && judgementData?.participants) {
            const judges = judgementData?.participants[selectedChestNumber.value as unknown as number]?.judges;
            const scores = judgementData?.participants[selectedChestNumber.value as unknown as number]?.score;
            const eventType = judgementData.eventType;
            const participantID = eventType !== 'Group' ? judgementData?.participants[selectedChestNumber.value as unknown as number].participantId : null;
            const groupID = eventType == 'Group' ? judgementData?.participants[selectedChestNumber.value as unknown as number].groupId : '';

            const allColumnsList = [{
                id: "0",
                name: 'Criteria'
            }];

            if (judges) {
                for (const [judgeId, judgeName] of Object.entries(judges)) {
                    allColumnsList.push({
                        id: judgeId,
                        name: judgeName,
                    });
                }
                setAllColumns(allColumnsList);
            }
            const items: ItemsType[] = [];
            if (scores) {
                const resultList: ResultList[] = [];
                for (const [criteriaName, scoreObj] of Object.entries(scores)) {
                    const result: ItemsType = {
                        criteriaId: scoreObj.criteriaId,
                        criteriaName: criteriaName,
                        maxScore: scoreObj.maxScore,
                    };
                    for (const [judgeId, judgeScoreObj] of Object.entries(scoreObj?.judges)) {
                        // Transform score for display: (score * MAX_SCORE) / actualMaxScore
                        const actualMaxScore = scoreObj.maxScore;
                        const storedScore = parseFloat(judgeScoreObj.score);
                        const displayScore = storedScore ? ((storedScore * MAX_SCORE) / actualMaxScore).toString() : '';
                        
                        result[judgeId] = {
                            score: displayScore,
                            judgeId,
                            maxScore: scoreObj.maxScore,
                        };
                        // Store the display score in resultList (user-entered values on 0-10 scale)
                        resultList.push({
                            criteriaId: scoreObj.criteriaId,
                            judgeId: judgeId,
                            score: displayScore,
                        });
                    }
                    items.push(result);
                }
                setTableItems(items);
                setSaveScoreResultRequest((prevScore) => ({
                    ...prevScore,
                    participantID: participantID ?? undefined,
                    groupID,
                    chestNumber: selectedChestNumber.value,
                    resultList,
                }));

            }
        }
    }, [judgementData?.participants, selectedChestNumber.value]);

    useEffect(() => {
        if (allColumns) {
            const dynamicColumnDefs: TableProps.ColumnDefinition<ItemsType>[] = allColumns.map((item) => {
                return {
                    header: item.name,
                    cell: (cellItem) => {
                        if (item.id === "0") {
                            return <ValueWithLabel label={cellItem.criteriaName}><Badge color="green">Max score: {MAX_SCORE}</Badge></ValueWithLabel>
                        } else {
                            const isInvalid = cellItem[item.id].score > MAX_SCORE || cellItem[item.id].score < 0 || isNaN(cellItem[item.id].score);
                            return <Input invalid={isInvalid} type='number' value={cellItem[item.id].score} onChange={({ detail }) => handleScoreChange(detail.value, item.id, cellItem.criteriaId, cellItem[item.id].maxScore)} />;
                        }
                    }
                }
            });
            setColumnDefinitions(dynamicColumnDefs);
        }
    }, [allColumns, tableItems, isTableValid]);


    if (!judgementData) {
        return <Spinner />;
    }

    if (chestNumber) {
        return (
            <>
                <Modal
                    onDismiss={() => setModalVisible(false)}
                    visible={isModalVisible}
                    header="Scores"
                    size="small"
                >
                    Scores are not valid. Please verify again!
                </Modal>
                <ExpandableSection
                    defaultExpanded
                    variant="container"
                    headerText="Scores"
                >
                    <SpaceBetween direction="vertical" size="l">
                        {
                            isSuccess && (
                                <Alert type='success'>Scores updated successfully</Alert>
                            )
                        }
                        {
                            error && (
                                <Alert type='error'>An error occurred while updating scores. Please try again!</Alert>
                            )
                        }
                        {
                            chestNumber && (
                                <FormField label="Chest number">
                                    <Select
                                        selectedOption={selectedChestNumber}
                                        onChange={({ detail }) => {
                                            setSelectedChestNumber(detail.selectedOption);
                                        }}
                                        options={chestNumber.map((chestNum) => ({
                                            label: chestNum, value: chestNum
                                        }))}
                                    />
                                </FormField>
                            )
                        }
                        {
                            !!tableItems.length && (
                                <Table
                                    columnDefinitions={columnDefinitions}
                                    items={tableItems}
                                    loadingText="Loading resources"
                                    empty={
                                        <Box
                                            margin={{ vertical: "xs" }}
                                            textAlign="center"
                                            color="inherit"
                                        >
                                            <SpaceBetween size="m">
                                                <b>No data found for specified event and grade</b>
                                            </SpaceBetween>
                                        </Box>
                                    }
                                    header={<Header actions={
                                        <Button variant="primary" onClick={saveScores} loading={isPending}>
                                            Save scores
                                        </Button>
                                    }>Scores</Header>}
                                />
                            )
                        }

                    </SpaceBetween>
                </ExpandableSection>
            </>
        )
    }
    return null;
};

